import { createContext, type ReactNode, useContext } from 'react'

import { cn } from '../cn'

// List + ListItem (design/components/list.html). For collections without a
// column structure: team, contacts, settings menus. Two densities; rows that
// navigate are real <a>/<button> (focusable), the chevron is decorative. Long
// text truncates but the full value stays available via title. For tabular,
// sortable data use DataTable instead.

type Density = 'comfortable' | 'dense'
const DensityCtx = createContext<Density>('comfortable')

export interface ListProps {
  density?: Density
  children: ReactNode
  className?: string
  'aria-label'?: string
}

/** Bordered <ul> for column-less collections (team, contacts, settings);
 *  `density` reaches every ListItem via context. For sortable tabular data use
 *  DataTable instead. */
export function List({ density = 'comfortable', children, className, ...rest }: ListProps) {
  return (
    <DensityCtx.Provider value={density}>
      <ul
        className={cn('overflow-hidden rounded-lg border border-line bg-surface', className)}
        {...rest}
      >
        {children}
      </ul>
    </DensityCtx.Provider>
  )
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[15px] text-text-3"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  )
}

export interface ListItemProps {
  leading?: ReactNode
  title: string
  subtitle?: string
  meta?: ReactNode
  href?: string
  onClick?: () => void
  className?: string
}

export function ListItem({
  leading,
  title,
  subtitle,
  meta,
  href,
  onClick,
  className,
}: ListItemProps) {
  const density = useContext(DensityCtx)
  const nav = href != null || onClick != null
  const pad = density === 'dense' ? 'gap-2.5 px-[14px] py-2' : 'gap-3 px-4 py-3'

  const inner = (
    <>
      {leading && <span className="flex-none">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-text" title={title}>
          {title}
        </span>
        {subtitle && (
          <span className="mt-0.5 block truncate text-[11px] text-text-2">{subtitle}</span>
        )}
      </span>
      {(meta != null || nav) && (
        <span className="ml-auto flex flex-none items-center gap-[11px] text-[11.5px] text-text-3">
          {meta}
          {nav && <Chevron />}
        </span>
      )}
    </>
  )

  const rowBase = cn('flex items-center', pad)
  const navCls =
    'transition-colors hover:bg-hover focus-visible:bg-hover focus-visible:outline-none'

  return (
    <li className="border-b border-line last:border-b-0">
      {href ? (
        <a href={href} className={cn(rowBase, navCls, className)}>
          {inner}
        </a>
      ) : onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(rowBase, navCls, 'w-full text-left', className)}
        >
          {inner}
        </button>
      ) : (
        <div className={cn(rowBase, className)}>{inner}</div>
      )}
    </li>
  )
}
